import React from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const Partnership = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  return (
    <div className="partnership-page">
      {/* Hero Section */}
      <section className="partnership-hero">
        <div className="container">
          <div className="partnership-hero-content">
            <h1 className="partnership-title">{t('partnership.hero.title')}</h1>
            <p className="partnership-subtitle">
              {t('partnership.hero.subtitle')}
            </p>
            <div className="partnership-cta">
              <button className="btn btn-primary">{t('partnership.hero.ctaButton')}</button>
            </div>
            <div className="partnership-features">
              <div className="feature-highlight">
                <i className="fas fa-users"></i>
                <span>{t('partnership.hero.features.clients')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-award"></i>
                <span>{t('partnership.hero.features.platform')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-chart-line"></i>
                <span>{t('partnership.hero.features.markets')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">{t('partnership.howItWorks.title')}</h2>
          <p className="section-description">
            {t('partnership.howItWorks.description1')}
          </p>
          <p className="section-description">
            {t('partnership.howItWorks.description2')}
          </p>
          <div className="income-highlight">
            <h3>{t('partnership.howItWorks.incomeHighlight')}</h3>
          </div>
        </div>
      </section>

      {/* Why River Prime Section */}
      <section className="why-river-prime">
        <div className="container">
          <h2 className="section-title">{t('partnership.whyRiverPrime.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-tachometer-alt"></i>
              </div>
              <h3>{t('partnership.whyRiverPrime.benefits.tracking')}</h3>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <h3>{t('partnership.whyRiverPrime.benefits.profits')}</h3>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-analytics"></i>
              </div>
              <h3>{t('partnership.whyRiverPrime.benefits.analytics')}</h3>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-headset"></i>
              </div>
              <h3>{t('partnership.whyRiverPrime.benefits.support')}</h3>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-layers"></i>
              </div>
              <h3>{t('partnership.whyRiverPrime.benefits.accounts')}</h3>
            </div>
          </div>
          
          <div className="partnership-download">
            <h3>{t('partnership.whyRiverPrime.download.title')}</h3>
            <p>{t('partnership.whyRiverPrime.download.description')}</p>
            <div className="download-buttons">
              <button className="btn btn-secondary">{t('partnership.whyRiverPrime.download.guideButton')}</button>
              <button className="btn btn-secondary">{t('partnership.whyRiverPrime.download.agreementButton')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('partnership.quickHelp.title')}</h2>
          
          <div className="help-sections">
            {/* Experienced Trader Section */}
            <div className="help-section">
              <h3 className="help-title">{t('partnership.quickHelp.experienced.title')}</h3>
              <div className="help-cards">
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-handshake"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.experienced.partner.title')}</h4>
                  <p>{t('partnership.quickHelp.experienced.partner.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-globe-americas"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.experienced.markets.title')}</h4>
                  <p>{t('partnership.quickHelp.experienced.markets.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.experienced.events.title')}</h4>
                  <p>{t('partnership.quickHelp.experienced.events.description')}</p>
                </div>
              </div>
            </div>

            {/* New Trader Section */}
            <div className="help-section">
              <h3 className="help-title">{t('partnership.quickHelp.newTrader.title')}</h3>
              <div className="help-cards">
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-mouse-pointer"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.newTrader.platform.title')}</h4>
                  <p>{t('partnership.quickHelp.newTrader.platform.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-chart-bar"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.newTrader.charts.title')}</h4>
                  <p>{t('partnership.quickHelp.newTrader.charts.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-play-circle"></i>
                  </div>
                  <h4>{t('partnership.quickHelp.newTrader.demo.title')}</h4>
                  <p>{t('partnership.quickHelp.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="partnership-final-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('partnership.finalCta.title')}</h2>
            <p>{t('partnership.finalCta.description')}</p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary btn-large"
                onClick={(e) => handleButtonClick('becomePartner', e)}
              >
                {t('partnership.finalCta.partnerButton')}
              </button>
              <button 
                className="btn btn-secondary btn-large"
                onClick={(e) => handleButtonClick('contactUs', e)}
              >
                {t('partnership.finalCta.contactButton')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partnership;